<template>
  <q-table
    stripe
    class="mdi-card"
    color="primary"
    row-key="id"
    selection="multiple"
    separator="horizontal"
    dense
    :resource="resource"
    :filter="filter"
    :loading="loading"
    :columns="columns"
    :data="serverData"
    @request="request"
    :pagination.sync="storePagination"
    :selected.sync="selected"
    :rows-per-page-options="[0, 25, 50, 100, 250]"
    :visible-columns.sync="visibleColumnsModel"
  >
    <div slot="top" slot-scope="props" class="full-width q-pt-sm q-pb-sm">
      <transition
        mode="out-in"
        enter-active-class="animated flipInX"
        leave-active-class="animated flipOutX"
      >
        <!-- SEARCH -->
        <div
          v-if="!selectedRows.length"
          key="search-bar"
          class="row flex-center gutter-md"
          style="animation-duration: 0.4s"
        >
          <div class="col-6">
            <auto-saver :duration="900" :d-bounce="handleSearch" :value="search">
              <q-input
                slot-scope="props"
                :loading="props.isSaving"
                :before="[{icon: 'search'}]"
                placeholder="Search all records"
                color="secondary"
                class="full-width no-shadow q-body-1"
                clearable
                v-model="search"
                hide-underline
                @keydown.esc="search=''"
              />
            </auto-saver>
          </div>
          <!-- ICONS -->
          <div class="col-3 text-right">
            <q-table-columns
              class="q-body-1 full-width"
              v-model="visibleColumnsModel"
              label="Visible Columns"
              :columns="columns"
            />
          </div>
          <div class="col text-right">
            <q-btn flat small round icon="file_download">
              <q-tooltip>Download Excel</q-tooltip>
              <!-- <q-icon name="file_download" color="secondary"></q-icon> -->
              <q-popover>
                <q-list separator link>
                  <q-item v-close-overlay @click.native="handleExcel">Download CSV</q-item>
<!--                  <q-item v-close-overlay @click.native="handleExcelPretty">Download CSV Pretty</q-item>-->
                </q-list>
              </q-popover>
            </q-btn>
            <!-- <q-btn flat small round @click="handleExcel">
              <q-tooltip>Download Excel</q-tooltip>
              <q-icon name="file_download" color="secondary"></q-icon>
            </q-btn>-->
            <q-btn
              flat
              round
              dense
              :icon="props.inFullscreen ? 'fullscreen_exit' : 'fullscreen'"
              @click="props.toggleFullscreen"
            />
          </div>
        </div>
        <!-- BULK -->
        <div
          key="bulk-update-bar"
          class="row flex-center gutter-md"
          style="animation-duration: 0.4s"
          v-else
        >
          <div class="col" v-if="!readonly">
            <q-select
              flat
              icon="edit"
              :options="[{label: '', value: ''}].concat(columnOptions)"
              v-model="selectedColumnsAction"
              placeholder="Select a field to mass update"
              hide-underline
              class="q-mr-sm no-shadow q-body-1"
              inverted-light
              :color="inputBgColor"
            ></q-select>
          </div>
          <div class="col" v-if="selectedColumnsAction && !readonly">
            <component
              :is="batchUpdateComponentColumn.component.is || 'q-input'"
              v-bind="batchUpdateComponentColumn.component.props"
              clearable
              v-model="selectedColumnsValue"
              :placeholder="batchUpdateComponentColumn.label"
              :column="batchUpdateComponentColumn"
              hide-underline
              class="no-shadow q-body-1"
              inverted-light
              :color="inputBgColor"
              @change="selectedColumnsValue = $event"
            />
          </div>
          <div class="col-1" v-if="!readonly">
            <q-btn
              v-if="selectedColumnsAction"
              :disabled="selectedColumnsValue === null || selectedColumnsValue === undefined"
              color="secondary"
              class="no-shadow on-right"
              push
              @click.native.prevent="handleBulkUpdate"
            >Apply</q-btn>
          </div>
          <div class="col text-right">
            <q-btn
              v-if="trashed"
              color="positive"
              flat
              label="Restore Selected"
              icon="restore"
              @click="handleBatchRestore"
            ></q-btn>
            <q-btn
              v-else
              color="negative"
              flat
              delete
              label="Delete Selected"
              icon="delete"
              @click="handleBatchDelete"
            ></q-btn>
          </div>
        </div>
      </transition>
    </div>
    <filter-bar
      slot="top-row"
      slot-scope="props"
      :cols="props.cols"
      :resource="resource"
      @change="request({pagination: storePagination})"
    />
    <template
      v-for="(column, columnIndex) in visibleColumns"
      :slot="`body-cell-${column.name}`"
      slot-scope="{row, value}"
    >
      <q-td :key="columnIndex" :align="column.align">
        <grid-cell
          :column="column"
          :row="row"
          :id="row.id"
          :value="value"
          :resource="resource"
          :trashed="trashed"
          :client-route="clientRoute"
          :readonly="readonly"
        ></grid-cell>
      </q-td>
    </template>
  </q-table>
</template>
<script>
  import {extend} from 'quasar'
  import colors from '../../mixins/colors'

  export default {
  name: 'LayoutTable',
  mixins: [colors],
  props: ['resource', 'trashed', 'readonly'],
  data () {
    return {
      loaded: false,
      filter: '',
      ready: false,
      loading: false,
      selectedColumnsAction: null,
      selectedColumnsValue: null
    }
  },
  computed: {
    serverData () {
      return this.$store.state[this.resource].records
    },
    selectedRows () {
      return this.$store.getters[`${this.resource}/selectedRows`]
    },
    storePagination: {
      get () {
        return this.$store.state[this.resource].settings.pagination
      },
      set (p) {
        this.$store.commit(`${this.resource}/SET_PAGINATION`, p)
      }
    },
    selected: {
      get () {
        return this.selectedRows
      },
      set (payload) {
        this.$store.commit(`${this.resource}/SET_SETTING`, {
          field: 'selected',
          value: payload
        })
      }
    },
    search: {
      get () {
        return this.settings && this.settings.filters ? this.settings.filters.search : ''
      },
      set (payload) {
        this.$store.commit(`${this.resource}/SET_SEARCH`, payload)
      }
    },
    visibleColumnsModel: {
      get () {
        return this.visibleColumns.reduce((arr, column) => {
          if (column && column.field) {
            arr.push(column.field)
          }
          return arr
        }, [])
      },
      set (payload) {
        // // console.log('...visible', payload)
        this.$store.commit(`${this.resource}/SET_SETTING`, {
          field: 'visibleColumns',
          value: payload
        })
      }
    },
    listLayout () {
      switch (this.layout) {
        case 'cards':
          return 'layout-cards'
        case 'accordion':
          return 'layout-accordion'
        default:
          return 'layout-table'
      }
    },
    maxPage () {
      return Math.ceil(this.storePagination.rowsNumber / this.storePagination.rowsPerPage || 1)
    },
    clientRoute () {
      return this.$store.state[this.resource].clientRoute
    },
    filters () {
      return this.$store.getters[`${this.resource}/filters`]
    },
    settings () {
      return this.$store.getters[`${this.resource}/settings`]
    },
    relations () {
      return this.$store.getters[`${this.resource}/relations`]
    },
    columns () {
      return this.$store.getters[`${this.resource}/columns`]
    },
    column () {
      return this.$store.getters[`${this.resource}/column`]
    },
    visibleColumns () {
      return this.$store.getters[`${this.resource}/visibleColumns`]
    },
    filterColumns () {
      return this.$store.getters[`${this.resource}/filterColumns`]
    },
    columnOptions () {
      return this.columns
        .filter(c => c.component && c.component.is)
        .map(c => {
          return { label: c.label, value: c.field }
        })
    },
    batchUpdateComponentColumn () {
      return this.columns.find(c => c.field === this.selectedColumnsAction)
    }
  },
  methods: {
    async handleSearch (v) {
      // console.log('search!')
      await this.request({ pagination: this.storePagination, filter: v })
    },
    async request ({ pagination = {}, filter = '' }) {
      // // console.log('pagination', pagination)
      this.loading = true
      try {
        // // console.log('dispatch?')
        await this.$store.dispatch(`${this.resource}/paginate`, {
          client: this.$axios,
          pagination,
          search: filter,
          trashed: this.trashed ? 1 : null
        })
        this.loading = false
        return Promise.resolve()
      } catch (e) {
        this.loading = false
        // console.log(e)
        return Promise.resolve()
      }
    },
    handleExcel () {
      const BASE_URL = process.env.API_BASE_URL
      const token = this.$store.getters['auth/token']
      const endpoint = this.$store.state[`${this.resource}`].excelUrl
      const fullUrl = `${BASE_URL}${endpoint}&token=${token}`
      console.log('fullUrl', fullUrl)
      this.$download(fullUrl)
    },
    handleExcelPretty () {
      const BASE_URL = process.env.API_BASE_URL
      const token = this.$store.getters['auth/token']
      const endpoint = this.$store.state[`${this.resource}`].excelPrettyUrl
      const fullUrl = `${BASE_URL}${endpoint}&token=${token}`
      this.$download(fullUrl)
    },
    /* ----------- CRUD -------------------- */
    async handleBulkUpdate () {
      try {
        await this.$q.dialog({
          title: `Apply Bulk Update`,
          message: `You are about to apply a bulk update to ${this.selected.length} items. ${this.batchUpdateComponentColumn.label}: ${this.selectedColumnsValue}`,
          ok: {
            color: 'positive',
            label: 'Confirm Bulk Update',
            outline: true
          },
          cancel: true
        })
        this.loading = true
        await this.$store.dispatch(`${this.resource}/apiBulkUpdate`, {
          id: this.selected.map(c => c.id),
          field: this.selectedColumnsAction,
          value: this.selectedColumnsValue,
          relation: !!this.batchUpdateComponentColumn.relation,
          fk: this.batchUpdateComponentColumn.relation ? this.batchUpdateComponentColumn.relation.fk : null,
          type: this.batchUpdateComponentColumn.relation ? this.batchUpdateComponentColumn.relation.type : null,
          client: this.$axios
        })
        this.$nextTick(() => {
          this.$q.notify({
            message: 'Bulk Update Complete',
            type: 'positive'
          })
        })
        this.loading = false
      } catch (e) {
        this.loading = false
      }
    },
    async handleDelete (id) {
      try {
        if (this.trashed) {
          await this.$store.dispatch(`${this.resource}/apiForceDelete`, { id, client: this.$axios })
        } else {
          await this.$store.dispatch(`${this.resource}/apiDelete`, { id, client: this.$axios })
        }
        const deleteRecordIndex = this.serverData.findIndex(data => (data.id === id))
        this.$delete(this.serverData, deleteRecordIndex)
        this.$q.notify('Deleted')
        // this.populateList()
      } catch (e) {
        // // console.log(e)
      }
    },
    async handleBatchDelete () {
      try {
        await this.$q.dialog({
          title: `Bulk Delete Warning`,
          message: `You are about to delete to ${this.selected.length} items. Are you sure?`,
          ok: {
            color: 'positive',
            label: 'Confirm Bulk Delete',
            outline: true
          },
          cancel: true
        })
        this.loading = true
        const selectedBefore = this.selected
        await this.$store.dispatch(`${this.resource}/apiPurge`, {
          client: this.$axios,
          id: this.selected.map(c => c.id)
        })
        for (let selected of selectedBefore) {
          const deleteRecordIndex = this.serverData.findIndex(data => (data.id === selected.id))
          this.$delete(this.serverData, deleteRecordIndex)
        }
        this.selected = []
        this.loading = false
        this.$q.notify({
          message: 'Bulk Delete Complete',
          type: 'positive'
        })
      } catch (e) {
        // console.log('error', e)
        this.loading = false
      }
    },
    async handleBatchRestore () {
      try {
        await this.$q.dialog({
          title: `Bulk Restore Warning`,
          message: `You are about to restore to ${this.selected.length} items. Are you sure?`,
          ok: {
            color: 'positive',
            label: 'Confirm Restore',
            outline: true
          },
          cancel: true
        })
        this.loading = true
        const selectedBefore = this.selected
        await this.$store.dispatch(`${this.resource}/apiRestore`, {
          client: this.$axios,
          id: this.selected.map(c => c.id)
        })
        for (let selected of selectedBefore) {
          const deleteRecordIndex = this.serverData.findIndex(data => (data.id === selected.id))
          this.$delete(this.serverData, deleteRecordIndex)
        }
        this.selected = []
        // await this.request({ pagination: this.storePagination, filter: this.filters })
        this.$q.notify({
          message: 'Restore Complete',
          type: 'positive'
        })
        this.loading = false
      } catch (e) {
        this.loading = false
        // console.log(e)
      }
    },
    async handleSave ({ id, field, value }) {
      const data = { [field]: value }
      this.$store
        .dispatch(`${this.resource}/apiUpdate`, { id, client: this.$axios, data })
        .then(() => {
          this.$q.notify('Updated')
        })
        .catch(e => {
          // // console.log(e)
        })
    },
    async handleSort ({ field, direction }) {
      const pagination = extend(true, this.storePagination, {
        sortBy: field,
        descending: direction < 0
      })
      await this.request({ pagination })
    }
  }
}
</script>
